import React from 'react';
import { clsx } from 'clsx'; // または classnames

type ButtonProps = {
    children: React.ReactNode;
    variant?:
        | 'default'
        | 'primary'
        | 'secondary'
        | 'outline'
        | 'text'
        | 'danger'
        | 'success'
        | 'warning'
        | 'original'
        | 'outlineDanger';
    size?: 'sm' | 'md' | 'lg';
    className?: string;
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    loadingLabel?: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const Button: React.FC<ButtonProps> = ({
    children,
    variant = 'default',
    size = 'md',
    className,
    isLoading = false,
    leftIcon,
    rightIcon,
    loadingLabel,
    disabled,
    ...props
}) => {
    const base = 'inline-flex items-center justify-center font-medium rounded-md transition gap-2';
    const variants = {
        original: 'bg-kotomilThemaColor opacity-90 hover:opacity-100 text-black',
        default: 'bg-gray-200 text-black hover:bg-gray-300',
        primary: 'bg-blue-600 text-white hover:bg-blue-700',
        secondary: 'bg-gray-600 text-white hover:bg-gray-700',
        outline: 'border border-gray-300 text-black hover:bg-gray-100',
        text: 'text-blue-600 hover:bg-gray-100',
        danger: 'bg-red-600 text-white hover:bg-red-700',
        outlineDanger: 'border border-red-600 hover:bg-red-300',
        success: 'bg-green-600 text-white hover:bg-green-700',
        warning: 'bg-yellow-500 text-white hover:bg-yellow-600',
    };
    const sizes = {
        sm: 'text-sm px-3 py-1.5 w-20 h-10',
        md: 'text-base px-4 py-2 w-20 h-10',
        lg: 'text-lg px-5 py-3 w-20 h-10',
    };
    const isDisabled = disabled || isLoading;

    return (
        <button
            className={clsx(
                base,
                variants[variant],
                sizes[size],
                isDisabled && 'opacity-60 cursor-not-allowed',
                className,
            )}
            disabled={isDisabled}
            {...props}
        >
            {leftIcon && <span className="flex items-center">{leftIcon}</span>}
            <span className="flex items-center">
                {isLoading ? (
                    <span className="animate-pulse">{loadingLabel ?? 'Loading...'}</span>
                ) : (
                    children
                )}
            </span>
            {rightIcon && <span className="flex items-center">{rightIcon}</span>}
        </button>
    );
};
